import { useEffect, useRef, useState } from 'react';
import { BridgeClient } from '../../../app/service/bridge/BridgeClient';
import { IndexingStatus } from '../../home/operatorState';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';

const POLL_INTERVAL_MS = 3000;

function normalizeIndexingStatus(rawStatus) {
  if (rawStatus === 'done' || rawStatus === 'completed') return IndexingStatus.READY;
  if (rawStatus === 'running' || rawStatus === 'queued') return IndexingStatus.RUNNING;
  if (rawStatus === 'error' || rawStatus === 'failed') return IndexingStatus.ERROR;
  return IndexingStatus.IDLE;
}

function parseIndexingState(response) {
  const readiness = response?.state?.readiness || null;
  const indexing = response?.state?.indexing || null;
  // Wizard Ready path: a completed usable index is the only authoritative
  // ready signal. Bridge v2 surfaces this as `readiness.can_search === true`
  // (see BridgeUseCase::handleOnboardingState). The 15 baseline tests
  // keep working because they assert `active_index_present === true`
  // alongside `status === 'completed'`, which still maps to READY.
  const canSearch = readiness?.can_search === true;
  return {
    indexingStatus: canSearch
      ? IndexingStatus.READY
      : normalizeIndexingStatus(indexing?.status ?? null),
    indexingProgress: typeof indexing?.progress === 'number'
      ? Math.max(0, Math.min(100, indexing.progress))
      : 0,
    activeIndexPresent: canSearch || indexing?.active_index_present === true,
    error: indexing?.error ? String(indexing.error) : null,
    rawIndexingStatus: typeof indexing?.status === 'string' ? indexing.status : null,
    jobId: typeof indexing?.job_id === 'string' && indexing.job_id.length > 0
      ? indexing.job_id
      : null,
    readinessCanSearch: canSearch,
  };
}

function isInitialQueuedJob(parsed) {
  return parsed?.rawIndexingStatus === 'queued'
    && !parsed.activeIndexPresent
    && !parsed.error
    && typeof parsed.jobId === 'string'
    && /^[A-Za-z0-9_.-]{1,64}$/.test(parsed.jobId);
}

function hasInitialDispatchOrRecovery(response, jobId) {
  const indexing = response?.data?.indexing;
  if (response?.result !== 'success' || indexing?.job_id !== jobId
    || (response.job_id != null && response.job_id !== jobId)) return false;

  const currentStatus = ['ok', 'unknown', 'deferred'].includes(response.status);
  if (!currentStatus) {
    // Legacy already-running compatibility is observation, not launch proof.
    return response.status === undefined && indexing.status === 'running';
  }
  // A handled request alone does not attest dispatch. Exact-target running
  // or usable-index evidence can also supersede a nonlaunch race.
  return response.status === 'ok'
    || indexing.status === 'running'
    || indexing.active_index_present === true;
}

/**
 * useIndexingProgress — polls onboarding.state every 3s and exposes
 * plugin-owned indexing status to the caller.


 * Behaviour:
 * - isLoading: true until the first poll resolves.
 * - Auto-trigger (idle/no-index path): on the first successful poll where
 *   activeIndexPresent=false and status is idle, dispatches indexing.start
 *   once. The guard resets only if the dispatch fails or retrigger() is
 *   called.
 * - Queued initial/no-index path: manually admits the existing full job
 *   through indexing.start, then kicks only the same still-queued identity.
 *   Admission is idempotent; it does not create a duplicate queued full job.
 *   Refusal is visible and requires retrigger(), not a polling retry loop.
 * - Running path: shows in-progress state; no duplicate indexing.start;
 *   no repeated index.kick spam.
 * - Ready path: treats readiness.can_search=true (or completed usable
 *   index) as READY; no start/kick calls are made.
 * - Failed path: exposes retrigger() to retry; retry calls indexing.start
 *   (which reuses the existing failed job or creates a new one — see
 *   BridgeUseCase P03 contract).
 * - Lock-contention path: if index.kick returns success with a "another
 *   worker is already running" message, the hook keeps polling and does
 *   not show a fatal error.
 * - Stops polling on terminal status (READY or ERROR) and when the plugin
 *   is degraded (result:'error' response).
 * - retrigger(): resets the trigger guard, clears the kicked-job-id set,
 *   resets state, and restarts polling. Use this when the operator clicks
 *   a retry button after an indexing error.
 * - Cleans up interval on unmount — no memory leaks.


 * Returns:
 *   indexingStatus     — IndexingStatus enum value
 *   indexingProgress   — 0–100
 *   activeIndexPresent — bool
 *   error              — error string or null (plugin-reported or hook-level)
 *   isLoading          — bool
 *   isDegraded         — bool (plugin too old to support onboarding.state)
 *   jobId              — string|null, current job_id from bridge (or null)
 *   rawIndexingStatus  — string|null, raw `indexing.status` from bridge
 *                        (lets consumers distinguish 'queued' vs 'running')
 *   readinessCanSearch — bool, the authoritative ready signal from bridge
 *   retrigger          — fn
 */
export function useIndexingProgress() {
  const t = useLocalizedMessage();
  const [indexingStatus, setIndexingStatus] = useState(IndexingStatus.IDLE);
  const [indexingProgress, setIndexingProgress] = useState(0);
  const [activeIndexPresent, setActiveIndexPresent] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDegraded, setIsDegraded] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [rawIndexingStatus, setRawIndexingStatus] = useState(null);
  const [readinessCanSearch, setReadinessCanSearch] = useState(false);
  const [generation, setGeneration] = useState(0); // bumped by retrigger()

  const triggerSentRef = useRef(false);
  // One initial identity per setup attempt; only explicit retry resets it.
  const initialJobIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;
    let latestIndexing = null;
    let admissionFailure = null;
    let nextPollId = 0;
    let appliedPollId = 0;

    const bridge = new BridgeClient();
    triggerSentRef.current = false;
    initialJobIdRef.current = null;
    setIsLoading(true);
    setError(null);
    setIsDegraded(false);
    setIndexingStatus(IndexingStatus.IDLE);
    setIndexingProgress(0);
    setActiveIndexPresent(false);
    setJobId(null);
    setRawIndexingStatus(null);
    setReadinessCanSearch(false);

    const sendStartIfIdle = (parsed) => {
      if (
        !triggerSentRef.current
        && initialJobIdRef.current === null
        && !parsed.activeIndexPresent
        && parsed.indexingStatus === IndexingStatus.IDLE
      ) {
        triggerSentRef.current = true;
        bridge.sendMessageWithResponse('indexing.start', {}).catch(() => {
          // Allow a future poll or explicit retrigger() to try again
          if (!cancelled) triggerSentRef.current = false;
        });
      }
    };

    const isCurrentInitialJob = (id) => !cancelled
      && isInitialQueuedJob(latestIndexing)
      && latestIndexing.jobId === id;

    const admitInitialJob = async (parsed) => {
      if (!isInitialQueuedJob(parsed) || initialJobIdRef.current !== null) return;
      const id = parsed.jobId;
      initialJobIdRef.current = id;
      try {
        const response = await bridge.sendMessageWithResponse('indexing.start', {});
        if (!isCurrentInitialJob(id)) return;
        const admitted = response?.data?.indexing;
        const returnedId = response?.job_id ?? admitted?.job_id;
        if (response?.result !== 'success' || returnedId !== id
          || (admitted?.job_id != null && admitted.job_id !== id)) {
          throw new Error('Initial indexing admission was not confirmed.');
        }
        // Another actor may have started or finished this exact job while
        // admission was in flight. Never kick running/terminal work.
        if (['running', 'completed', 'done', 'failed', 'error'].includes(admitted?.status)) return;
        if (admitted?.status !== 'queued') throw new Error('Initial indexing state was not confirmed.');
        const dispatch = await bridge.sendMessageWithResponse('index.kick', { job_id: id });
        if (!hasInitialDispatchOrRecovery(dispatch, id)) throw new Error('Initial indexing dispatch was not confirmed.');
      } catch (_error) {
        if (!isCurrentInitialJob(id)) return;
        admissionFailure = { jobId: id, message: t('ui.integrationWizard.step5Summary.indexingFailed') };
        setError(admissionFailure.message);
        setIndexingStatus(IndexingStatus.ERROR);
      }
    };

    const poll = async () => {
      const pollId = ++nextPollId;
      try {
        const response = await bridge.sendMessageWithResponse('onboarding.state', {});
        if (cancelled || pollId < appliedPollId) return;
        appliedPollId = pollId;
        if (response.result === 'error') {
          latestIndexing = null;
          setIsDegraded(true);
          setIsLoading(false);
          window.clearInterval(intervalId);
          return;
        }

        const parsed = parseIndexingState(response);
        latestIndexing = parsed;
        if (!isInitialQueuedJob(parsed) || admissionFailure?.jobId !== parsed.jobId) admissionFailure = null;
        setIndexingStatus(admissionFailure ? IndexingStatus.ERROR : parsed.indexingStatus);
        setIndexingProgress(parsed.indexingProgress);
        setActiveIndexPresent(parsed.activeIndexPresent);
        setError(parsed.error || admissionFailure?.message || null);
        setJobId(parsed.jobId);
        setRawIndexingStatus(parsed.rawIndexingStatus);
        setReadinessCanSearch(parsed.readinessCanSearch);
        setIsLoading(false);
        admitInitialJob(parsed);
        sendStartIfIdle(parsed);
        if (
          parsed.indexingStatus === IndexingStatus.READY
          || parsed.indexingStatus === IndexingStatus.ERROR
        ) {
          window.clearInterval(intervalId);
        }
      } catch (err) {
        if (!cancelled && pollId >= appliedPollId) {
          setError(err.message || t('catalog.update.statusReadFailed'));
          setIsLoading(false);
        }
      }
    };

    poll();
    intervalId = window.setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [generation, t]); // re-run when retrigger() bumps generation

  const retrigger = () => {
    // A deliberate retry can admit the same queued job or observe a new one.
    triggerSentRef.current = false;
    initialJobIdRef.current = null;
    setGeneration((g) => g + 1);
  };

  return {
    indexingStatus,
    indexingProgress,
    activeIndexPresent,
    error,
    isLoading,
    isDegraded,
    jobId,
    rawIndexingStatus,
    readinessCanSearch,
    retrigger,
  };
}
